// src/pages/Contact.js
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import styles from './Contact.module.css';

const Contact = () => {
  const [formData, setFormData] = useState({ name: '', email: '', message: '' });
  const [agreed, setAgreed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!agreed) {
      setError('Please agree to the Privacy Policy to continue.');
      return;
    }
    setLoading(true);
    setError('');
    setSuccess('');

    fetch(`${process.env.REACT_APP_API_URL}/api/contact/`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(formData),
    })
      .then((res) => {
        if (res.ok) return res.json();
        throw new Error('Failed to send message');
      })
      .then(() => {
        setSuccess('Your message has been sent successfully!');
        setFormData({ name: '', email: '', message: '' });
        setAgreed(false);
      })
      .catch((err) => {
        setError('Failed to send message. Please try again.');
        console.error(err);
      })
      .finally(() => setLoading(false));
  };

  const infoItems = [
    { icon: '✉️', label: 'Email', value: 'info@nulekconsulting.com.au', href: 'mailto:info@nulekconsulting.com.au' },
    { icon: '📞', label: 'Phone', value: '+61 413 613 265', href: 'tel:+61413613265' },
    { icon: '📍', label: 'Location', value: 'Australia-wide', href: null },
  ];

  return (
    <div className={styles.page}>
      {/* Hero Section */}
      <section className={styles.hero}>
        <div className={styles.heroAccentBar} />
        <div className={styles.heroRadialBg} />
        <div className={styles.heroContent}>
          <p className={styles.heroEyebrow}>Get in Touch</p>
          <h1 className={styles.heroTitle}>Contact Us</h1>
          <p className={styles.heroLead}>Have questions? Reach out to our team and we'll get back to you promptly.</p>
        </div>
      </section>

      {/* Main Content */}
      <section className={styles.body}>
        <div className={styles.grid}>
          {/* Left: Info Cards */}
          <div className={styles.infoCol}>
            <h2 className={styles.infoHeading}>How to Reach Us</h2>
            <p className={styles.infoText}>
              We're available to discuss your project needs, answer questions about our services, or schedule a free consultation.
            </p>
            {infoItems.map((item, i) => (
              <div key={i} className={styles.infoCard}>
                <div className={styles.infoIconWrap}>{item.icon}</div>
                <div>
                  <p className={styles.infoLabel}>{item.label}</p>
                  {item.href ? (
                    <a href={item.href} className={styles.infoValueLink}>
                      {item.value}
                    </a>
                  ) : (
                    <p className={styles.infoValueText}>{item.value}</p>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Right: Form Card */}
          <div className={styles.formCard}>
            <div className={styles.formAccentBar} />

            {success ? (
              <div className={styles.successBanner}>
                <span className={styles.successIcon}>✅</span>
                <div>
                  <p className={styles.successTitle}>Message sent!</p>
                  <p className={styles.successText}>{success}</p>
                </div>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className={styles.form}>
                <div className={styles.formGroup}>
                  <label className={styles.label}>Your Name</label>
                  <input
                    type="text"
                    name="name"
                    className={styles.input}
                    placeholder="e.g. Alex Johnson"
                    value={formData.name}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className={styles.formGroup}>
                  <label className={styles.label}>Email Address</label>
                  <input
                    type="email"
                    name="email"
                    className={styles.input}
                    placeholder="alex@company.com.au"
                    value={formData.email}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className={styles.formGroup}>
                  <label className={styles.label}>Message</label>
                  <textarea
                    name="message"
                    className={styles.textarea}
                    rows="5"
                    placeholder="Tell us about your project or enquiry..."
                    value={formData.message}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className={styles.checkboxGroup}>
                  <input
                    type="checkbox"
                    id="privacyCheck"
                    className={styles.checkbox}
                    checked={agreed}
                    onChange={(e) => setAgreed(e.target.checked)}
                    required
                  />
                  <label htmlFor="privacyCheck" className={styles.checkboxLabel}>
                    I agree to the{' '}
                    <Link to="/legal/privacy" target="_blank" className={styles.privacyLink}>
                      Privacy Policy
                    </Link>{' '}
                    and consent to the collection of my data.
                  </label>
                </div>

                <button
                  type="submit"
                  className={styles.submitButton}
                  disabled={loading || !agreed}
                >
                  {loading ? (
                    <span className={styles.loadingSpinner}>
                      <span className={styles.spinner} />
                      Sending...
                    </span>
                  ) : (
                    'Send Message'
                  )}
                </button>
              </form>
            )}

            {error && (
              <div className={styles.errorBanner}>
                <span>⚠️</span>
                <span>{error}</span>
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;